import { api } from 'api/api';
import { Endpoints } from 'api/constants';
import { Translations } from 'types/Common';
import {
  IBaseNewItem,
  INewItem,
  INewsCategory,
  IComment,
  INewsRecipient,
} from 'types/income';
import { APP } from 'config';
import { exportXlsxConfig, getBinaryValue, getSorting } from 'utils/general';
import { ContentLanguage, TableDataResponse } from 'types/general';
import { AssignmentStatus } from 'store/assignments';
import {
  NewsCategoriesFilters,
  NewsCategoryTranslations,
  NewsCommentsFilters,
  NewsFilters,
  NewsRecipientsFilters,
  NewsSendMethod,
  NewsStatus,
} from './news.types';

const getFilters = (filters: Partial<NewsFilters>) => ({
  page: filters.page,
  pageSize: filters.pageSize,
  ...getSorting(filters.sort),
  search: filters.search || undefined,
  filter: {
    status: filters.status,
    publishDateFrom: filters.publishedAt?.from,
    publishDateTo: filters.publishedAt?.to,
    categoryIds: filters.categories,
    employeeGroupIds: filters.employeeGroups,
    notificationMethods: filters.sendMethods,
    pinned: getBinaryValue(filters.pinned),
    allowComments: getBinaryValue(filters.allowComments),
  },
});

const getCommentsFilters = (filters: Partial<NewsCommentsFilters>) => ({
  page: filters.page,
  pageSize: filters.pageSize,
  ...getSorting(filters.sort),
  search: filters.search || undefined,
  filter: {
    status: filters.status,
    dateFrom: filters.date?.from,
    dateTo: filters.date?.to,
    employeeIds: filters.employees,
    employeeGroupIds: filters.employeeGroups,
    newsIds: filters.news,
    newsCategoryIds: filters.newsCategories,
    reactions: filters.reactions,
  },
});

const getRecipientsFilters = (filters: Partial<NewsRecipientsFilters>) => ({
  page: filters.page,
  pageSize: filters.pageSize,
  ...getSorting(filters.sort),
  search: filters.search || undefined,
  filter: {
    status: filters.assignmentStatus,
    fromEmployeeCount: filters.employeeCount?.from,
    toEmployeeCount: filters.employeeCount?.to,
    companyIds: filters.companies,
    companyGroupIds: filters.companyGroups,
  },
});

interface GetNews {
  filters: Partial<NewsFilters>;
}

interface GetNew {
  newId: string;
}

interface CreateNew {
  name: string;
  defaultLanguage: Uppercase<ContentLanguage>;
}

export interface UpdateNew {
  newId: string;
  update: Partial<
    Omit<CreateNew, 'defaultLanguage'> & {
      publishedAt: string | null;
      inactiveAt: string | null;
      sendMethods: NewsSendMethod[];
      translations: Translations<{
        title: string;
        content: string;
        headerImageId: string | null;
        attachmentIds: string[];
      }>;
      pinned: boolean;
      pinnedUntil: string | null;
      allowComments: boolean;
      categories: string[];
      draft: boolean;
    }
  >;
}

interface PublishNewNow {
  newId: string;
}

interface CopyNew {
  newId: string;
  name: string;
}

interface RemoveNew {
  newId: string;
}

interface BulkUpdateNews {
  ids: string[];
  data: Partial<Pick<UpdateNew['update'], 'inactiveAt' | 'allowComments'>>;
}

interface BulkDeleteNews {
  ids: string[];
}

interface ExportXls {
  filters: Partial<NewsFilters>;
}

interface GetCategories {
  filters: Partial<NewsCategoriesFilters>;
}

interface GetCategory {
  categoryId: string;
}

interface CreateCategory {
  name: string;
  translations: NewsCategoryTranslations;
}

interface UpdateCategory {
  categoryId: string;
  name: string;
  translations: NewsCategoryTranslations;
}

interface DeleteCategory {
  categoryId: string;
}

interface GetComments {
  filters: Partial<NewsCommentsFilters>;
}

interface GetRecipients {
  newId: string;
  filters: Partial<NewsCommentsFilters>;
}

interface AssignRecipients {
  newId: string;
  employeeGroupIds: string[];
  status: AssignmentStatus;
}

export const loaders = {
  getNews: ({ filters }: GetNews) => {
    return api<TableDataResponse<IBaseNewItem>>({
      url: Endpoints.clientAdmin.news.root,
      version: 'v2',
      params: {
        ...getFilters(filters),
      },
    });
  },
  getNew: ({ newId }: GetNew) => {
    return api<INewItem>({
      url: Endpoints.clientAdmin.news.byId.root.replace(':newId', newId),
    });
  },
  createNew: ({ name, defaultLanguage }: CreateNew) => {
    return api<{ id: string; name: string }>({
      url: Endpoints.clientAdmin.news.create,
      method: 'post',
      data: { name },
    }).then(({ data }) => {
      return loaders.updateNew({
        newId: data.id,
        update: {
          translations: [
            {
              language: defaultLanguage,
              title: data.name,
              content: '',
              headerImageId: null,
              attachmentIds: [],
            },
          ],
        },
      });
    });
  },
  updateNew: ({ newId, update }: UpdateNew) => {
    return api<{ id: string; status: NewsStatus }>({
      url: Endpoints.clientAdmin.news.byId.root.replace(':newId', newId),
      method: 'patch',
      data: {
        name: update.name,
        draft: update.draft,
        pinned: update.pinned,
        pinnedUntil: update.pinnedUntil,
        allowComments: update.allowComments,
        inactiveDate: update.inactiveAt,
        publishDate: update.publishedAt,
        sendMethods: update.sendMethods,
        translations: update.translations,
        categoryIds: update.categories,
      },
    });
  },
  publishNewNow: ({ newId }: PublishNewNow) => {
    return api<{ id: string; status: NewsStatus }>({
      url: Endpoints.clientAdmin.news.byId.publishNow.replace(':newId', newId),
      method: 'post',
    });
  },
  copyNew: ({ newId, name }: CopyNew) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.news.byId.copy.replace(':newId', newId),
      params: { name },
      method: 'post',
    });
  },
  deleteNew: ({ newId }: RemoveNew) => {
    return api({
      url: Endpoints.clientAdmin.news.byId.root.replace(':newId', newId),
      method: 'delete',
    });
  },
  bulkUpdateNews: ({ ids, data }: BulkUpdateNews) => {
    return api({
      url: Endpoints.clientAdmin.news.bulk.root,
      data: {
        ids,
        allowComments: data.allowComments,
        inactiveDate: data.inactiveAt,
      },
      method: 'patch',
    });
  },
  bulkDeleteNews: ({ ids }: BulkDeleteNews) => {
    return api({
      url: Endpoints.clientAdmin.news.bulk.root,
      data: {
        ids,
      },
      method: 'delete',
    });
  },
  exportXls: ({ filters }: ExportXls) => {
    const { pageSize, page, ...params } = getFilters(filters);

    return api({
      url: Endpoints.clientAdmin.news.export.xls,
      version: 'v2',
      params,
      ...exportXlsxConfig,
    });
  },
  getCategories: ({ filters }: GetCategories) => {
    return api<TableDataResponse<INewsCategory>>({
      url: Endpoints.clientAdmin.news.categories.root,
      params: {
        page: filters.page,
        pageSize: filters.pageSize ?? APP.pagination.defaults.pageSize[0],
      },
    });
  },
  getCategory: ({ categoryId }: GetCategory) => {
    return api<INewsCategory>({
      url: Endpoints.clientAdmin.news.categories.byId.replace(
        ':categoryId',
        categoryId,
      ),
    });
  },
  createCategory: ({ name, translations }: CreateCategory) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.news.categories.root,
      method: 'post',
      data: { name, translations },
    });
  },
  updateCategory: ({ categoryId, name, translations }: UpdateCategory) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.news.categories.byId.replace(
        ':categoryId',
        categoryId,
      ),
      method: 'put',
      data: { name, translations },
    });
  },
  deleteCategory: ({ categoryId }: DeleteCategory) => {
    return api({
      url: Endpoints.clientAdmin.news.categories.byId.replace(
        ':categoryId',
        categoryId,
      ),
      method: 'delete',
    });
  },
  getComments: ({ filters }: GetComments) => {
    return api<TableDataResponse<IComment>>({
      url: Endpoints.clientAdmin.news.comments.root,
      params: {
        ...getCommentsFilters(filters),
      },
    });
  },
  getRecipients: ({ newId, filters }: GetRecipients) => {
    return api<TableDataResponse<INewsRecipient>>({
      url: Endpoints.clientAdmin.news.byId.recipients.replace(':newId', newId),
      version: 'v2',
      params: {
        ...getRecipientsFilters(filters),
      },
    });
  },
  assignRecipients: ({ newId, employeeGroupIds, status }: AssignRecipients) => {
    return api<TableDataResponse<INewsRecipient>>({
      url: Endpoints.clientAdmin.news.byId.assignRecipients.replace(
        ':newId',
        newId,
      ),
      data: employeeGroupIds.map((id) => ({ employeeGroupId: id, status })),
      method: 'post',
    });
  },
};
